/**
 * What `StreamingText` needs to know about an answer before it loads the
 * Markdown renderer (`AnswerMarkdown`, a chunk of its own). Pure.
 */

/** A blank line, or a line that opens a heading, list, quote or table. */
const BLOCK = /\n[ \t]*\n|^[ \t]{0,3}(#{1,6}[ \t]|[-*+][ \t]|\d+[.)][ \t]|>|\|)/m;

/** Whether `text` has blocks to lay out, or is one paragraph. */
export const isFormatted = (text: string) => BLOCK.test(text.trim());

const isRow = (line: string) => /^\s*\|/.test(line);
const isDelimiter = (line: string) => /^\s*\|?(\s*:?-+:?\s*\|)+\s*:?-*:?\s*$/.test(line);

/**
 * While an answer arrives, a table is held back until it can be drawn as one:
 * before its delimiter row it would read as a line of pipes, and a row still
 * being written would draw with cells missing.
 */
export function withoutIncompleteTable(text: string): string {
  const lines = text.split('\n');
  let start = lines.length;
  while (start > 0 && isRow(lines[start - 1]!)) start--;
  if (start === lines.length) return text;
  const table = lines.slice(start);
  if (!table.some(isDelimiter)) return lines.slice(0, start).join('\n');
  const last = table.at(-1)!;
  return last.trimEnd().endsWith('|') ? text : lines.slice(0, -1).join('\n');
}

/**
 * Where the analyst placed a figure: a line of its own, `[[figure:<id>]]`. The
 * engine sends the figure itself beside the text; the line says where it goes.
 */
const MARKER = /^[ \t]*\[\[figure:([\w-]+)\]\][ \t]*$/;

export type AnswerPart = { kind: 'text'; text: string } | { kind: 'figure'; id: string };

/** The answer as text runs and the figures placed between them, in order. */
export function splitAtFigures(text: string): AnswerPart[] {
  const parts: AnswerPart[] = [];
  let run: string[] = [];
  const flush = () => {
    const joined = run.join('\n').trim();
    if (joined) parts.push({ kind: 'text', text: joined });
    run = [];
  };
  for (const line of text.split('\n')) {
    const id = MARKER.exec(line)?.[1];
    if (id) {
      flush();
      parts.push({ kind: 'figure', id });
    } else {
      run.push(line);
    }
  }
  flush();
  return parts;
}

/** The ids of the figures the answer places, in order. */
export const figureIds = (text: string) =>
  splitAtFigures(text).flatMap((part) => (part.kind === 'figure' ? [part.id] : []));

/** The answer with its marker lines removed, so no marker is ever shown as text. */
export const withoutFigureMarkers = (text: string) =>
  splitAtFigures(text)
    .flatMap((part) => (part.kind === 'text' ? [part.text] : []))
    .join('\n\n');
